import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { api, type User } from './api';

export const passkeysSupported = browserSupportsWebAuthn();

type OptionsResponse = { options: Record<string, unknown>; handle: string };

/**
 * A user cancelling Face ID throws the same way a real failure does. Translating
 * it here keeps "you tapped cancel" from being reported as a security error.
 */
export function describePasskeyError(err: unknown): string {
  if (err instanceof Error) {
    if (err.name === 'NotAllowedError') {
      return 'Cancelled, or the request timed out. Try again.';
    }
    if (err.name === 'InvalidStateError') {
      return 'This device already has a passkey for this account.';
    }
    return err.message;
  }
  return 'Something went wrong.';
}

export async function signInWithPasskey(): Promise<{ user: User }> {
  const { options, handle } = await api.post<OptionsResponse>('/api/auth/passkey/options');
  const response = await startAuthentication({ optionsJSON: options as never });
  return api.post<{ user: User }>('/api/auth/passkey/verify', { handle, response });
}

export type ClaimResult = {
  user: User;
  totpUri: string;
  recoveryCodes: string[];
  claimStillOpen: boolean;
};

export async function claimAccount(
  secret: string,
  displayName: string,
  deviceName: string,
): Promise<ClaimResult> {
  const { options, handle } = await api.post<OptionsResponse>('/api/auth/claim/options', {
    secret,
    displayName,
  });
  const response = await startRegistration({ optionsJSON: options as never });
  return api.post<ClaimResult>('/api/auth/claim/verify', { handle, response, deviceName });
}

export async function addPasskey(deviceName: string): Promise<void> {
  const { options, handle } = await api.post<OptionsResponse>('/api/auth/passkeys/options');
  const response = await startRegistration({ optionsJSON: options as never });
  await api.post('/api/auth/passkeys/verify', { handle, response, deviceName });
}
